
/* eslint-disable @typescript-eslint/camelcase */
import { Vue, prop, Options } from "vue-class-component";
import { KulionerMerchant, KulionerOrder } from "@/domain/entities/Kulioner";
import {
  CLIENT_ERROR,
  convertArrayToString,
  ConvertObjectCamelToSnakeCase,
  formatPriceRP,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import JsBarcode from "jsbarcode";
import { AccountController } from "@/app/ui/controllers/AccountController";
import generateThermalPDF from "@/app/ui/views/shipment/bulk-print-resi/modules/bulk-label-resi-thermal";
import generatePrintReceipt from "./receipt";
import ModalProcessKulioner from "./ModalProcessKulioner.vue";
import { KulionerController } from "@/app/ui/controllers/KulionerController";
import { PrintDataEntitas, ShipmentId } from "@/domain/entities/Booking";
import ModalSuccessBulkProcessOrder from "./modal-success-bulk-process-order.vue";
import { ResponsePayload } from "@/domain/entities/ResponsePayload";
import { ShipmentBookingController } from "@/app/ui/controllers/ShipmentBookingController";
import { ShipmentInstantBooking } from "@/data/payload/api/ShipmentBookingRequest";
import { LocationController } from "@/app/ui/controllers/LocationController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import generateKulionerBulkLabelResiThermal from "../modules/kulioner-bulk-label-resi-thermal";
import ModalProcessOrder from "./modal-process-order.vue";

class Props {
  selected = prop<KulionerOrder[]>({
    default: () => [],
    type: Array
  });
  status = prop<string>({
    default: "",
    type: String
  });
  isMerchant = prop<boolean>({
    default: false,
    type: Boolean
  });
  merchantData = prop<KulionerMerchant[]>({
    default: () => [],
    type: Array
  });
  shipmentIds = prop<string[]>({
    default: () => [],
    type: Array
  });
  canBeProcessed = prop<boolean>({
    default: false,
    type: Boolean
  });
}

@Options({
  components: {
    ModalProcessKulioner,
    ModalSuccessBulkProcessOrder,
    ModalProcessOrder
  }
})
class FooterListKulioner extends Vue.with(Props) {
  isOpenConfirmOrderModal = false;
  isShowModalProcess = false;

  get totalPrice() {
    const totalPrice = this.selected.reduce(
      (acc, curr) => acc + curr.totalPrice,
      0
    );

    return formatPriceRP(totalPrice);
  }

  get totalMerchant() {
    const merchantNames = this.selected.map((order) => order.koMerchantName);

    return [...new Set(merchantNames)].length;
  }

  get totalSelected() {
    const orderId = this.selected.map((order) => order.orderId);

    return [...new Set(orderId)].length;
  }

  get isShowPrintReceipt() {
    return ["ORDBKD", "ORDDLV", "ORDRCV"].includes(this.status.toUpperCase());
  }

  get isShowAcceptOrder() {
    return this.status.toUpperCase() === "ORDWTC";
  }

  get acceptOrderButtonText() {
    return `Terima pesanan (${this.selected.length})`;
  }

  get listSttSelected() {
    if (!this.selected.length) {
      return [];
    }
    return this.selected.filter((item) => item.orderSttNo);
  }

  // DEPRECATED
  get isEligibleProcessOrderByMerchant() {
    // all product should be isQuantityUpdated = true
    // actual quantity must be less than or equal to quantity
    return this.merchantData.every((merchant) => {
      const allActualQuantityZero = merchant.products.every(
        (product) => product.actualQuantity === 0
      );

      const eligibleProcess = !merchant.products.every(
        (product) =>
          product.isQuantityUpdated &&
          product.actualQuantity <= product.quantity
      )
      return allActualQuantityZero || eligibleProcess;
    });
  }

  listStt: PrintDataEntitas[] = [];
  async getDetailStt() {
    try {
      const listStt = await KulionerController.getListStt({
        page: 1,
        limit: 50,
        destinationCityId: 0,
        userType: "",
        bookedBy: "",
        bookedType: "",
        fromDate: "",
        endDate: "",
        sttNumber: convertArrayToString(this.listSttSelected, "orderSttNo"),
        isBulk: true
      });

      const listOrder = await Promise.allSettled(
        this.selected.map(
          async (selected) =>
            await KulionerController.getKulionerDetail(selected.id)
        )
      );

      const successData = listOrder.filter(
        (e) => e.status === "fulfilled"
      ) as PromiseFulfilledResult<KulionerOrder>[];

      this.listStt = successData
        .map((e) => e.value)
        .map((order) => {
          const sttDetail = listStt.find(
            (stt) => stt.sttNo === order.orderSttNo
          );
          return new PrintDataEntitas({
            ...sttDetail,
            orderDetail: order
          });
        });
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, "Gagal Print resi!")
      );
      throw err;
    }
  }

  async generateDataResi() {
    const list: PrintDataEntitas[] = [];

    await new Promise((resolve) => {
      this.listStt.forEach((item, index) => {
        if (item) {
          const barcode = document.createElement("img");
          const vendorBarcode = document.createElement("img");
          JsBarcode(barcode, item.sttNo, {
            textAlign: "left",
            displayValue: false,
            fontSize: 16
          });
          if (item.sttVendorRefferenceNo) {
            // vendor ref code
            JsBarcode(vendorBarcode, item.sttVendorRefferenceNo, {
              textAlign: "left",
              displayValue: false,
              fontSize: 16
            });
          }

          item.sttPieces.forEach((e) => {
            list.push(
              new PrintDataEntitas({
                ...e,
                ...item,
                barcode: barcode.src,
                vendorRefCode: vendorBarcode.src
              })
            );
          });

          barcode.remove();
          vendorBarcode.remove();
        }

        if (this.listStt.length - 1 === index) {
          resolve(true);
        }
      });
    });

    return list;
  }

  async printBulkReceipt() {
    const listBulkStt: PrintDataEntitas[] = await this.generateDataResi();
    if (!listBulkStt.length) return;

    new Promise(function(resolve) {
      MainAppController.showLoading();
      setTimeout(resolve.bind(null, null), 1);
    }).then(() => {
      const accountType = AccountController.accountData.account_type;
      generateKulionerBulkLabelResiThermal({
        data: listBulkStt,
        accountType
      });
    });
  }

  isLoadingPrintReceipt = false;
  async onPrintReceipt() {
    try {
      MainAppController.showLoading();
      this.isLoadingPrintReceipt = true;
      this.listStt = [];
      await this.getDetailStt();
      await this.printBulkReceipt();
    } finally {
      MainAppController.closeLoading();
      this.isLoadingPrintReceipt = false;
    }
  }

  get isLoadingModal() {
    return MainAppController.loadingModal;
  }
  async onPrintOrder() {
    MainAppController.showLoading();

    const res = await Promise.allSettled(
      this.selected.map(
        async (selected) =>
          await KulionerController.getKulionerDetail(selected.id)
      )
    );
    const successData = res.filter(
      (e) => e.status === "fulfilled"
    ) as PromiseFulfilledResult<KulionerOrder>[];
    const results = this.isMerchant ? Array(new KulionerOrder({ merchants: this.merchantData })) : successData.map((e) => e.value);
    await generatePrintReceipt(results, this.isMerchant);

    MainAppController.closeLoading();
  }

  onAcceptOrder() {
    this.isOpenConfirmOrderModal = true;
  }

  openModalSuccessBulkProcessOrder = false;
  isLoadingProcessOrder = false;
  bulkProcessResponse: ResponsePayload[] = [];
  sttProductType = "";
  async onProcessOrder() {
    this.isShowModalProcess = false;
    const errorList: any[] = [];
    this.bulkProcessResponse = [];
    const validSelectedList = this.selected.filter((item) =>
      item.orderShipmentId.startsWith("AD")
    );

    try {
      MainAppController.showLoading();
      if (!validSelectedList.length) return;

      MainAppController.showLoading();
      await Promise.allSettled(
        validSelectedList.map(async (selected) => {
          try {
            const res = await KulionerController.getShipmentIdList(
              selected.orderShipmentId
            );

            if (!res.data.length) {
              this.onStoreBulkResponseData(
                new ResponsePayload({ success: false }),
                selected
              );
              return;
            }

            for (const shipment of res.data) {
              const newSelected = new KulionerOrder({
                ...selected,
                orderShipmentId: shipment.shipmentId,
                productType: shipment.shipmentProduct,
                recipientName: shipment.shipmentRecipientName,
                orderId: shipment.shipmentId
              });
              await this.addInstantBooking(shipment, newSelected);
            }

          } catch (err) {
            errorList.push(err);

            this.onStoreBulkResponseData(
              new ResponsePayload({ success: false }),
              selected
            );
          }
        })
      );
    } finally {
      MainAppController.closeLoading();
      this.isLoadingProcessOrder = false;
    }

    this.showPopupResponse(errorList);
  }
  async addInstantBooking(shipment: ShipmentId, item: KulionerOrder) {
    try {
      const accountTypeDetail = this.dataProfile.account_type_detail;
      const res: ResponsePayload = await ShipmentBookingController.addInstantBooking(
        new ShipmentInstantBooking({
          shipmentPackageId: shipment.shipmentPacketId,
          stt: this.mappingPayloadStt(shipment),
          districtOrigin: {
            id: this.originDistrict.id,
            code: this.originDistrict.code,
            name: `${this.originDistrict.name}, ${this.originDistrict.cityName}`,
            ursaCode: this.originDistrict.ursaCode,
            city: {
              id: this.originDistrict.cityId,
              code: this.originDistrict.cityCode,
              name: this.originDistrict.cityName
            }
          },
          districtDestination: {
            id: shipment.shipmentDestinationDistrict.id,
            code: shipment.shipmentDestinationDistrict.code,
            name: shipment.shipmentDestinationDistrict.name,
            ursaCode: shipment.shipmentDestinationDistrict.ursa_code,
            isCod: "",
            status: "",
            type: "",
            city: {
              id: shipment.shipmentDestinationCity.id,
              code: shipment.shipmentDestinationCity.code,
              name: shipment.shipmentDestinationCity.name
            }
          },
          commodity: {
            commodityId: shipment.shipmentCommodityId,
            commodityCode: shipment.shipmentCommodityCode,
            commodityName: shipment.shipmentCommodityName,
            commodityDocumentSurcharge:
              shipment.shipmentCommodityDocumentSurcharge,
            hsCode: shipment.shipmentCommodityHsCode
          },
          partner: {
            id: accountTypeDetail.id,
            name: accountTypeDetail.company_name,
            code: accountTypeDetail.code,
            partnerExternalCode: this.dataProfile.account_type_detail
              ?.partnerExternalCode,
            partnerLocation: {
              id: accountTypeDetail.partnerLocation?.id,
              districtCode: accountTypeDetail.partnerLocation?.district_code
            },
            partnerLocationList: []
          }
        })
      );

      this.onStoreBulkResponseData(
        new ResponsePayload({ ...res, success: true }),
        item
      );
    } catch (error) {
      this.onStoreBulkResponseData(
        new ResponsePayload({
          success: false
        }),
        item
      );
    }
  }
  onStoreBulkResponseData(data: ResponsePayload, item: KulionerOrder) {
    this.bulkProcessResponse.push(
      new ResponsePayload({
        success: data.success,
        sttNumber: data.sttNumber ?? "",
        data: {
          sttShipmentId: item.orderId,
          estimationDate: data.data?.estimation_date ?? "",
          sttProductType: item.productType,
          sttRecipientName: item.recipientName,
          id: item.id
        }
      })
    );
  }
  showPopupResponse(errorList: any[]) {
    const failed =
      this.bulkProcessResponse.length &&
      this.bulkProcessResponse.every(
        (e) => !e.success && (!e.sttNumber || e.sttNumber === "")
      );
    MainAppController.closeLoading();

    if (failed) {
       this.showErrorProcessOrder(errorList[0]);
    } else {
      this.openModalSuccessBulkProcessOrder = true;
    }
  }
  showErrorProcessOrder(error: any) {
    MainAppController.showErrorMessage(
      new ErrorMessageEntities({
        title: "Booking gagal",
        message: error?.response?.data?.message?.id || "",
        type: CLIENT_ERROR,
        buttonSuccessText: "Oke, mengerti",
        onClose: () => {
          MainAppController.closeErrorMessage();
        }
      })
    );
  }
  mappingSttPieces(shipment: ShipmentId) {
    return shipment.shipmentGoodDetail.length
      ? shipment.shipmentGoodDetail.map((pieces: any) =>
          ConvertObjectCamelToSnakeCase({
            sttPieceLength: pieces.shipment_goods_length,
            sttPieceWidth: pieces.shipment_goods_width,
            sttPieceHeight: pieces.shipment_goods_height,
            sttPieceGrossWeight: pieces.shipment_goods_weight
          })
        )
      : [];
  }
  payloadSttOne(shipment: ShipmentId) {
    return {
      sttNo: "",
      sttShipmentId: shipment.shipmentAlgoId,
      sttTaxNumber: "",
      sttGoodsEstimatePrice: shipment.shipmentGoodsPriceEstimation,
      sttGoodsStatus: shipment.shipmentGoodsStatus,
      sttNoRefExternal: shipment.shipmentBookingId.startsWith("TSLP")
        ? shipment.shipmentBookingId
        : "",
      sttOriginCityId: this.originDistrict.cityCode,
      sttDestinationCityId: shipment.shipmentDestinationCity?.code,
      sttOriginDistrictId: this.originDistrict.code
    };
  }

  payloadSttTwo(shipment: ShipmentId) {
    this.sttProductType = shipment.shipmentProduct;
    return {
      sttDestinationDistrictId: shipment.shipmentDestinationDistrict?.code,
      sttSenderName: shipment.shipmentSenderName,
      sttSenderPhone: shipment.senderPhoneInstantBooking,
      sttSenderAddress: shipment.shipmentSenderAddress,
      sttRecipientName: shipment.shipmentRecipientName,
      sttRecipientPhone: shipment.recipientPhoneInstantBooking,
      sttRecipientAddress: shipment.shipmentRecipientAddress,
      sttProductType: shipment.shipmentProduct,
      sttCommodityCode: shipment.shipmentCommodityCode || "GEN",
      sttInsuranceType: shipment.shipmentInsurance
    };
  }

  payloadSttThree(shipment: ShipmentId) {
    return {
      sttIsDo: shipment.isDO,
      sttIsCod: shipment.shipmentIsCod,
      sttIsDfod: shipment.shipmentIsDfod,
      sttCodFee: shipment.codFee,
      sttCodAmount: shipment.codAmount,
      sttPieces: this.mappingSttPieces(shipment),
      postalCodeDestination: shipment.postalCodeDestination,
      addressReceiverType: "home",
      sttWoodpacking: shipment.shipmentIsWoodpacking,
      sttAttachFiles: [],
      sttRecipientEmail: "",
      sttKtpImage: "",
      sttTaxImage: "",
      sttInterTaxNumber: "",
      sttIdentityNumber: "",
      sttCipl: []
    };
  }
  mappingPayloadStt(shipment: ShipmentId) {
    return {
      ...this.payloadSttOne(shipment),
      ...this.payloadSttTwo(shipment),
      ...this.payloadSttThree(shipment)
    };
  }
  get dataProfile() {
    return AccountController.accountData;
  }
  get originDistrict() {
    return LocationController.districtDetail;
  }

  get isEligibleProcessOrderByCustomer() {
    return !this.selected.every((merchant) =>
      merchant.merchants.every((merchant) => merchant.isPurchased) && merchant.merchants.some((merchant) => merchant.actualQuantity)
    );
  }
}
export default FooterListKulioner;
